import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDate } from '../ui/format';
import { Badge, EmptyState, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { listKeyDates, removeKeyDate, type KeyDate, type KeyDateKind } from './api';

/**
 * The dates a relationship is made of (Phase 121).
 *
 * A birthday, the anniversary of a contract, the month a seasonal customer always buys: every one
 * of them is a reason to call somebody, and a CRM that knows them is one people open on a Monday
 * morning.
 *
 * Grouped by how close they are rather than listed by date, because "hoje" and "em 26 dias" are
 * different kinds of information: one is something to do now and the other is something to plan
 * around, and a single list sorted by date buries the first under the second.
 */

const KIND_TONE: Record<KeyDateKind, BadgeTone> = {
  BIRTHDAY: 'info',
  ANNIVERSARY: 'info',
  RENEWAL: 'warning',
  CONTRACT_END: 'danger',
  CUSTOM: 'neutral',
};

const WINDOWS = [7, 30, 90];

/** Where a record lives, so a date can be clicked through to the person it is about. */
const RECORD_PATH: Record<string, string> = {
  CONTACT: '/crm/contatos',
  COMPANY: '/crm/empresas',
  LEAD: '/crm/leads',
  OPPORTUNITY: '/crm/oportunidades',
};

export function KeyDatesPage(): React.JSX.Element {
  const client = useQueryClient();
  const [days, setDays] = useState(30);
  const [error, setError] = useState<string | null>(null);

  const dates = useQuery({ queryKey: ['key-dates', days], queryFn: () => listKeyDates(days) });

  const forget = useMutation({
    mutationFn: (id: string) => removeKeyDate(id),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['key-dates'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const all = dates.data?.dates ?? [];
  const now = all.filter((date) => date.daysAway <= 1);
  const later = all.filter((date) => date.daysAway > 1);

  return (
    <div className="view">
      <PageHeader
        title={t('keyDates.title')}
        description={t('keyDates.description')}
        actions={
          <select
            className="select select--compact"
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
            aria-label={t('keyDates.window')}
          >
            {WINDOWS.map((value) => (
              <option key={value} value={value}>
                {t('keyDates.nextDays').replace('{n}', String(value))}
              </option>
            ))}
          </select>
        }
      />

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <Panel title={t('keyDates.now')}>
        {now.length === 0 ? (
          <EmptyState title={t('keyDates.nothingToday')} />
        ) : (
          <DateList dates={now} onForget={(id) => forget.mutate(id)} />
        )}
      </Panel>

      <Panel title={t('keyDates.ahead')}>
        {dates.isLoading ? (
          <p className="muted">{t('state.loading')}</p>
        ) : later.length === 0 ? (
          <EmptyState title={t('keyDates.emptyTitle')} description={t('keyDates.emptyBody')} />
        ) : (
          <DateList dates={later} onForget={(id) => forget.mutate(id)} />
        )}
      </Panel>
    </div>
  );
}

function DateList({
  dates,
  onForget,
}: {
  dates: KeyDate[];
  onForget: (id: string) => void;
}): React.JSX.Element {
  return (
    <ul className="key-dates">
      {dates.map((date) => (
        <li key={`${date.entityType}:${date.entityId}:${date.id ?? date.kind}`}>
          <span className="key-dates__when">
            <span className="strong">{formatDate(date.occurrence)}</span>
            <span className="muted small">{distance(date)}</span>
          </span>

          <span className="key-dates__what">
            <span className="strong">{date.label}</span>
            {date.subject && date.subject !== date.label && (
              <Link
                className="muted small"
                to={`${RECORD_PATH[date.entityType] ?? '/crm'}/${date.entityId}`}
              >
                {date.subject}
              </Link>
            )}
            {date.notes && <span className="muted small">{date.notes}</span>}
          </span>

          <Badge tone={KIND_TONE[date.kind]}>{t(`keyDates.kind.${date.kind}`)}</Badge>

          {/* A birthday has no row to delete: it is the contact's own field. */}
          {date.id && (
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() => onForget(date.id!)}
            >
              {t('keyDates.forget')}
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

function distance(date: KeyDate): string {
  if (date.when === 'TODAY') return t('keyDates.today');
  if (date.when === 'TOMORROW') return t('keyDates.tomorrow');

  return t('keyDates.inDays').replace('{n}', String(date.daysAway));
}
